/**
 * Time Slot Panel Component (Calendly-style)
 * 
 * Displays available time slots for a selected date.
 * Shows when a date is clicked in the calendar.
 */

import React from 'react';

interface TimeSlot {
  date: string;
  time: string;
  start_time: string;
  end_time: string;
  clinician_id: string;
  clinician_name: string | null;
}

interface TimeSlotPanelProps {
  /** Selected date in YYYY-MM-DD format */
  selectedDate: string;
  /** Available slots for the selected date */
  slots: TimeSlot[];
  /** Called when a time slot is selected */
  onSelectSlot: (slot: TimeSlot) => void;
  /** Whether a specific vet is selected (to show vet name) */
  showVetName?: boolean;
}

const TimeSlotPanel: React.FC<TimeSlotPanelProps> = ({
  selectedDate,
  slots,
  onSelectSlot,
  showVetName = false,
}) => {
  // Format date as "Friday, January 23"
  const formatDateHeader = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format time from 24h to 12h format (09:00 -> 9:00am)
  const formatTime = (time24: string): string => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes}${ampm}`;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Date Header */}
      <div className="mb-4">
        <h3 className="text-base font-bold text-gray-900">
          {formatDateHeader(selectedDate)}
        </h3>
      </div>

      {/* Time Slots List */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {slots.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">No available times.</p>
            <p className="text-xs text-gray-400 mt-1">Try another date.</p>
          </div>
        ) : (
          slots.map((slot, index) => (
            <button
              key={`${slot.time}-${slot.clinician_id}-${index}`}
              onClick={() => onSelectSlot(slot)}
              className="w-full py-2.5 px-3 border border-blue-600 text-blue-600 rounded-md
                         font-semibold text-sm text-center hover:bg-blue-600 hover:text-white
                         transition-all duration-150 focus:outline-none focus:ring-2 
                         focus:ring-blue-500 focus:ring-offset-1"
            >
              {formatTime(slot.time)}
              {showVetName && slot.clinician_name && (
                <span className="block text-xs font-normal mt-0.5 opacity-80">
                  {slot.clinician_name}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default TimeSlotPanel;
